import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import TableV2 from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../utils/pdf/generatePdfReport";

/** ---------------------- Tipos ---------------------- */

type HeaderMode = "retorno" | "tipo";

type SaidasServicoConfig = {
  areaSlug: string;
  estoqueSlug: string;
  key: string;

  departamento: string;
  awaitDepartamento: string;

  title: string;
  reportTitle: string;
  fileNamePrefix: string;

  headerMode: HeaderMode;
  enableRetornoModal: boolean;
};

export type SaidaItemAPI = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  produto: {
    id: number;
    codigo: string;
    nome: string;
    posicao: string | null;
  };
};

export type RetornoSolicitacaoAPI = {
  id: number | null;
  status: "solicitado" | "aprovado" | "negado" | null;
  quantidade: number | null;
  motivo: string | null;
  observacao: string | null;
  motivo_negativa: string | null;
  criado_em: string | null;
  analisado_em: string | null;
};

export type SaidaMovAPI = {
  movimentacao: {
    id: number;
    tipo?: "servico" | "venda" | "consumo" | "outro" | string | null;
    tipo_full?: string | null;

    responsavel_id: number | null;
    responsavel_nome: string | null;

    departamento_origem: string | null;
    os_numero?: string | null;
    coletor_nome?: string | null;
    criado_em: string;

    retorno_solicitacao?: RetornoSolicitacaoAPI | null;
  };
  itens: SaidaItemAPI[];
};

type RespAPI = {
  ok: boolean;
  meta: {
    filtros: {
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC";
      limit: number;
      offset: number;
    };
    count: number;
  };
  data: SaidaMovAPI[];
};

type SaidaRowUI = string[];

type RetornoStatusUI = "none" | "solicitado" | "aprovado" | "negado";

type SaidaRowMeta = {
  movimentacaoId: number;
  produtoId: number;
  itemId: number;

  osNumero: string;
  tipo: string;
  tipoFull: string;

  codigo: string;
  nomeProduto: string;

  quantidade: number;
  custoUnitario: number;

  tecnico: string;

  responsavelId: number | null;
  responsavelNome: string | null;

  retornoSolicitacaoId: number | null;
  retornoStatus: RetornoStatusUI;
  retornoQuantidade: number | null;
  retornoMotivo: string | null;
  retornoObservacao: string | null;
  retornoMotivoNegativa: string | null;
  retornoCriadoEm: string | null;
  retornoAnalisadoEm: string | null;
};

type SaidasQuery = Partial<{
  date_from: string;
  date_to: string;
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

type AwaitResult = {
  changed: boolean;
  versao: number | null;
};

type ItemOSModal = {
  originalIndex: number;
  row: SaidaRowUI;
  meta: SaidaRowMeta;
};

type ModalState = {
  open: boolean;
  os: string;
  itens: ItemOSModal[];
};

type SelecoesState = Record<
  number,
  {
    marcado: boolean;
    quantidade: string;
  }
>;

/** ---------------------- Constantes ---------------------- */

const API_BASE = "https://api.eva-googlemarine.com";

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Configurações antigas + fallback dinâmico ---------------------- */

const SAIDAS_CONFIGS: SaidasServicoConfig[] = [
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-servico-e-comercial",
    key: "suprimentos-servico-comercial",
    departamento: "suprimentos",
    awaitDepartamento: "suprimentos",
    title: "Suprimentos - Baixas Serviços",
    reportTitle: "Relatório de Saídas por Serviço - Suprimentos",
    fileNamePrefix: "Suprimentos-Saidas-Servicos",
    headerMode: "retorno",
    enableRetornoModal: true,
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-servico-campos",
    key: "suprimentos-campos",
    departamento: "suprimentos-campos",
    awaitDepartamento: "suprimentos-campos",
    title: "Suprimentos Campos - Baixas Serviços",
    reportTitle: "Relatório de Saídas por Serviço - Suprimentos Campos",
    fileNamePrefix: "Suprimentos-Campos-Saidas-Servicos",
    headerMode: "retorno",
    enableRetornoModal: true,
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-tecnicos-e-laboratorio",
    key: "tecnicos",
    departamento: "tecnicos",
    awaitDepartamento: "tecnicos",
    title: "Laboratório - Baixas Serviços",
    reportTitle: "Relatório de Saídas por Serviço - Laboratório",
    fileNamePrefix: "Laboratorio-Saidas-Servicos",
    headerMode: "tipo",
    enableRetornoModal: false,
  },
  {
    areaSlug: "suprimentos",
    estoqueSlug: "estoque-p-uso-coorporativo",
    key: "recursos-humanos",
    departamento: "recursos-humanos",
    awaitDepartamento: "recursos-humanos",
    title: "Suprimentos - Saídas do Estoque p/ Uso Coorporativo",
    reportTitle: "Relatório de Saídas - Estoque p/ Uso Coorporativo",
    fileNamePrefix: "Uso-Coorporativo-Saidas",
    headerMode: "tipo",
    enableRetornoModal: false,
  },
  {
    areaSlug: "coordenacao",
    estoqueSlug: "coordenacao-rastreio-de-materiais",
    key: "coordenacao",
    departamento: "coordenacao",
    awaitDepartamento: "coordenacao",
    title: "Coordenação - Saídas de Materiais",
    reportTitle: "Relatório de Saídas - Coordenação",
    fileNamePrefix: "Coordenacao-Saidas",
    headerMode: "retorno",
    enableRetornoModal: false,
  },
];

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part === "p") return "p/";
      if (part === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getConfigByParams(
  areaSlug?: string,
  estoqueSlug?: string
): SaidasServicoConfig {
  const area = normalizeSlug(areaSlug);
  const estoque = normalizeSlug(estoqueSlug);

  const found = SAIDAS_CONFIGS.find(
    (config) => config.areaSlug === area && config.estoqueSlug === estoque
  );

  if (found) return found;

  const safeArea = area || "suprimentos";
  const safeEstoque = estoque || "estoque";
  const estoqueTitle = slugToTitle(safeEstoque);

  return {
    areaSlug: safeArea,
    estoqueSlug: safeEstoque,
    key: `${safeArea}-${safeEstoque}`,
    departamento: safeEstoque,
    awaitDepartamento: safeEstoque,
    title: `${slugToTitle(safeArea)} - Saídas Serviços - ${estoqueTitle}`,
    reportTitle: `Relatório de Saídas por Serviço - ${estoqueTitle}`,
    fileNamePrefix: `${safeArea}-${safeEstoque}-Saidas-Servicos`,
    headerMode: "retorno",
    enableRetornoModal: false,
  };
}

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  const d = new Date(iso);

  if (!Number.isNaN(d.getTime())) {
    return {
      data: d.toLocaleDateString("pt-BR", {
        timeZone: "America/Sao_Paulo",
      }),
      hora: d.toLocaleTimeString("pt-BR", {
        timeZone: "America/Sao_Paulo",
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  }

  const m = iso.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
  );

  if (!m) {
    return {
      data: iso,
      hora: "",
    };
  }

  const [, ano, mes, dia, hora, minuto] = m;

  return {
    data: `${dia}/${mes}/${ano}`,
    hora: `${hora}:${minuto}`,
  };
}

function formatBRL(valor: number): string {
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseQuantidade(value: unknown): number {
  const n = Number(String(value ?? "0").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function parseBRLCurrency(value: unknown): number {
  const raw = String(value ?? "")
    .replace(/[^\d,.-]/g, "")
    .trim();

  if (!raw) return 0;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");

  let normalized = raw;

  if (lastComma > lastDot) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    normalized = raw.replace(/,/g, "");
  }

  const n = Number(normalized);

  return Number.isFinite(n) ? n : 0;
}

function normalizeRetornoStatus(
  status: RetornoSolicitacaoAPI["status"] | undefined | null
): RetornoStatusUI {
  if (status === "solicitado") return "solicitado";
  if (status === "aprovado") return "aprovado";
  if (status === "negado") return "negado";
  return "none";
}

function isSaidaServico(mov: SaidaMovAPI["movimentacao"]): boolean {
  const tipoFull = String(mov.tipo_full ?? "").toLowerCase();
  const tipo = String(mov.tipo ?? "").toLowerCase();

  if (tipoFull === "standby-servico-saida") return true;
  if (tipoFull.includes("servico") && tipoFull.includes("saida")) return true;
  if (tipoFull.includes("serviço") && tipoFull.includes("saida")) return true;
  if (tipo === "servico" || tipo === "serviço") return true;

  return false;
}

function getHeaders(config: SaidasServicoConfig): string[] {
  if (config.headerMode === "tipo") {
    return [
      "OS",
      "Tipo",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Custo Unitário",
      "Técnico",
      "Data",
      "Hora",
    ];
  }

  return [
    "OS",
    "Código",
    "Nome do Produto",
    "Quantidade",
    "Custo Unitário",
    "Técnico",
    "C. Responsável",
    "Data",
    "Hora",
  ];
}

function getColumnWidths(config: SaidasServicoConfig): string[] {
  if (config.headerMode === "tipo") {
    return [
      "60px",
      "80px",
      "70px",
      "300px",
      "50px",
      "70px",
      "90px",
      "90px",
      "60px",
    ];
  }

  return [
    "60px",
    "70px",
    "300px",
    "50px",
    "90px",
    "90px",
    "140px",
    "90px",
    "60px",
  ];
}

function flattenToRows(
  movs: SaidaMovAPI[],
  config: SaidasServicoConfig
): {
  rows: SaidaRowUI[];
  rowMeta: SaidaRowMeta[];
} {
  const rows: SaidaRowUI[] = [];
  const rowMeta: SaidaRowMeta[] = [];

  for (const mov of movs || []) {
    if (!isSaidaServico(mov.movimentacao)) continue;

    const { data, hora } = toBRT(mov.movimentacao?.criado_em || "");

    const os = mov.movimentacao?.os_numero || "-";
    const tipo = mov.movimentacao?.tipo || "-";
    const tipoFull = mov.movimentacao?.tipo_full || "-";
    const tecnico = mov.movimentacao?.coletor_nome || "-";
    const responsavel = mov.movimentacao?.responsavel_nome || "-";

    const retorno = mov.movimentacao?.retorno_solicitacao || null;
    const retornoStatus = normalizeRetornoStatus(retorno?.status);

    for (const item of mov.itens || []) {
      const quantidade = Number(item.quantidade || 0);
      const custo = Number(item.custo_unitario ?? "0");

      const codigo = String(item.produto?.codigo ?? "").toUpperCase();
      const nomeProduto = String(item.produto?.nome ?? "").toUpperCase();

      if (config.headerMode === "tipo") {
        rows.push([
          os,
          tipo,
          codigo,
          nomeProduto,
          String(Number.isFinite(quantidade) ? quantidade : 0),
          `R$ ${formatBRL(Number.isFinite(custo) ? custo : 0)}`,
          tecnico,
          data,
          hora,
        ]);
      } else {
        rows.push([
          os,
          codigo,
          nomeProduto,
          String(Number.isFinite(quantidade) ? quantidade : 0),
          `R$ ${formatBRL(Number.isFinite(custo) ? custo : 0)}`,
          tecnico,
          responsavel,
          data,
          hora,
        ]);
      }

      rowMeta.push({
        movimentacaoId: mov.movimentacao.id,
        produtoId: item.produto.id,
        itemId: item.id,

        osNumero: os,
        tipo: String(tipo),
        tipoFull: String(tipoFull),

        codigo,
        nomeProduto,

        quantidade: Number.isFinite(quantidade) ? quantidade : 0,
        custoUnitario: Number.isFinite(custo) ? custo : 0,

        tecnico,

        responsavelId: mov.movimentacao.responsavel_id,
        responsavelNome: mov.movimentacao.responsavel_nome,

        retornoSolicitacaoId: retorno?.id ?? null,
        retornoStatus,
        retornoQuantidade: retorno?.quantidade ?? null,
        retornoMotivo: retorno?.motivo ?? null,
        retornoObservacao: retorno?.observacao ?? null,
        retornoMotivoNegativa: retorno?.motivo_negativa ?? null,
        retornoCriadoEm: retorno?.criado_em ?? null,
        retornoAnalisadoEm: retorno?.analisado_em ?? null,
      });
    }
  }

  return {
    rows,
    rowMeta,
  };
}

function buildQuery(config: SaidasServicoConfig, query?: SaidasQuery): string {
  const params = new URLSearchParams();

  params.set("estoque_slug", config.estoqueSlug);

  if (query?.date_from) params.set("date_from", query.date_from);
  if (query?.date_to) params.set("date_to", query.date_to);
  if (query?.order) params.set("order", query.order);
  if (typeof query?.limit === "number")
    params.set("limit", String(query.limit));
  if (typeof query?.offset === "number")
    params.set("offset", String(query.offset));

  const s = params.toString();

  return s ? `?${s}` : "";
}

function getSaidasUrl(config: SaidasServicoConfig): string {
  return `${API_BASE}/departamentos/${encodeURIComponent(
    config.departamento
  )}/saidas`;
}

function getAwaitUrl(config: SaidasServicoConfig, since: number): string {
  const params = new URLSearchParams();

  params.set("since", String(since));
  params.set("timeout_ms", String(AWAIT_TIMEOUT_MS));
  params.set("estoque_slug", config.estoqueSlug);

  return `${API_BASE}/departamentos/${encodeURIComponent(
    config.awaitDepartamento
  )}/saidas/await?${params.toString()}`;
}

function getSolicitarRetornoUrl(config: SaidasServicoConfig): string {
  return `${API_BASE}/departamentos/${encodeURIComponent(
    config.departamento
  )}/retornos/solicitar`;
}

function getCancelarRetornoUrl(config: SaidasServicoConfig): string {
  return `${API_BASE}/departamentos/${encodeURIComponent(
    config.departamento
  )}/retornos/cancelar`;
}

/** ---------------------- Fetchers ---------------------- */

async function fetchSaidasOnce(
  config: SaidasServicoConfig,
  query?: SaidasQuery
): Promise<{
  rows: SaidaRowUI[];
  rowMeta: SaidaRowMeta[];
  meta: RespAPI["meta"] | null;
}> {
  const res = await fetch(
    `${getSaidasUrl(config)}${buildQuery(config, query)}`,
    {
      method: "GET",
      headers: buildAuthHeaders(),
      cache: "no-store",
    }
  );

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  const json = (await res.json()) as RespAPI;

  if (!json.ok) {
    return {
      rows: [],
      rowMeta: [],
      meta: json.meta ?? null,
    };
  }

  const flattened = flattenToRows(
    Array.isArray(json.data) ? json.data : [],
    config
  );

  return {
    rows: flattened.rows,
    rowMeta: flattened.rowMeta,
    meta: json.meta ?? null,
  };
}

async function awaitChange(
  config: SaidasServicoConfig,
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const res = await fetch(getAwaitUrl(config, since), {
      method: "GET",
      headers: buildAuthHeaders(),
      signal,
    });

    if (!res.ok) return null;

    const json = await res.json();

    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;

    const changed = Boolean(json?.changed);

    return {
      changed,
      versao,
    };
  } catch {
    return null;
  }
}

/** ---------------------- Hook genérico ---------------------- */

function useSaidasServicoGenerico(
  config: SaidasServicoConfig,
  query?: SaidasQuery
) {
  const [rows, setRows] = useState<SaidaRowUI[]>([]);
  const [rowMeta, setRowMeta] = useState<SaidaRowMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  async function carregar() {
    fetching.current = true;

    try {
      setErro(null);

      const result = await fetchSaidasOnce(config, query);

      setRows(result.rows);
      setRowMeta(result.rowMeta);
      setMeta(result.meta);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar saídas.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    setLoading(true);
    setErro(null);
    setRows([]);
    setRowMeta([]);
    setMeta(null);

    (async () => {
      try {
        await carregar();
        setLoading(false);

        const initResp = await awaitChange(config, -1);

        if (initResp && typeof initResp.versao === "number") {
          versaoRef.current = initResp.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();

            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;

            try {
              resp = await awaitChange(config, cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                await sleep(jitter(ERROR_BACKOFF_MS));
              }
            } finally {
              if (awaitCtrlRef.current === ac) {
                awaitCtrlRef.current = null;
              }

              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null) {
                awaitSupported.current = false;
              }

              await sleep(POLL_FALLBACK_MS);
              continue;
            }

            if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);

            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useSaidasServicoGenerico loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.key,
    config.areaSlug,
    config.estoqueSlug,
    config.departamento,
    JSON.stringify(query),
  ]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    rows,
    rowMeta,
    loading,
    refresh,
    meta,
    erro,
  };
}

/** ---------------------- Modal styles ---------------------- */

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 1080,
  background: "#ffffff",
  borderRadius: 0,
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  overflow: "hidden",
  border: "1px solid #0c2b53",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const modalHeaderStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderBottom: "1px solid #0c2b53",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#f8fafc",
};

const modalTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: "#111827",
};

const modalBodyStyle: React.CSSProperties = {
  padding: 20,
  overflowY: "auto",
};

const modalFooterStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderTop: "1px solid #0c2b53",
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  background: "#FFFFFF",
};

const buttonSecondaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#ffffff",
  color: "#111827",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

const buttonPrimaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#0c2b53",
  color: "#ffffff",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

const buttonMutedStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#f8fafc",
  color: "#0c2b53",
  borderRadius: 0,
  padding: "8px 12px",
  fontSize: 13,
  cursor: "pointer",
};

const buttonDangerStyle: React.CSSProperties = {
  border: "1px solid #7D1A1A",
  background: "#ffffff",
  color: "#7D1A1A",
  borderRadius: 0,
  padding: "8px 12px",
  fontSize: 13,
  cursor: "pointer",
};

const badgeBaseStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #0c2b53",
  borderRadius: 0,
  padding: "10px 12px",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 100,
  resize: "vertical",
  fontFamily: "inherit",
};

function getStatusLabel(status: RetornoStatusUI) {
  switch (status) {
    case "solicitado":
      return "p/ retorno";
    case "aprovado":
      return "Aprovado";
    case "negado":
      return "Negado";
    default:
      return "Sem solicitação";
  }
}

function getStatusBadgeStyle(status: RetornoStatusUI): React.CSSProperties {
  switch (status) {
    case "solicitado":
      return {
        ...badgeBaseStyle,
        background: "#FFD6CD",
        color: "#92400e",
        border: "1px solid #060606",
      };
    case "aprovado":
      return {
        ...badgeBaseStyle,
        background: "#dcfce7",
        color: "#166534",
        border: "1px solid #86efac",
      };
    case "negado":
      return {
        ...badgeBaseStyle,
        background: "#f8d7da",
        color: "#991b1b",
        border: "1px solid #f5a3ad",
      };
    default:
      return {
        ...badgeBaseStyle,
        background: "#f3f4f6",
        color: "#374151",
        border: "1px solid #d1d5db",
      };
  }
}

function canRequestStatus(status: RetornoStatusUI) {
  return status === "none" || status === "negado";
}

function canCancelStatus(status: RetornoStatusUI) {
  return status === "solicitado";
}

/** ---------------------- Página ---------------------- */

const SaidasServicoGenerico: React.FC = () => {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug: string;
    estoqueSlug: string;
  }>();

  const config = useMemo(
    () => getConfigByParams(areaSlug, estoqueSlug),
    [areaSlug, estoqueSlug]
  );

  const headers = useMemo(() => getHeaders(config), [config]);
  const columnWidths = useMemo(() => getColumnWidths(config), [config]);

  const { rows, rowMeta, loading, refresh, erro } =
    useSaidasServicoGenerico(config);

  const [modal, setModal] = useState<ModalState>({
    open: false,
    os: "",
    itens: [],
  });

  const [selecao, setSelecao] = useState<SelecoesState>({});
  const [motivo, setMotivo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cancelandoIds, setCancelandoIds] = useState<number[]>([]);

  const enabledColumnIndexes = useMemo(
    () => headers.map((_header, index) => index),
    [headers]
  );

  const totalProdutos = useMemo(() => {
    const quantidadeIndex = headers.indexOf("Quantidade");

    if (quantidadeIndex < 0) return 0;

    return rows.reduce((acc, row) => {
      return acc + parseQuantidade(row[quantidadeIndex]);
    }, 0);
  }, [headers, rows]);

  const valorTotal = useMemo(() => {
    const quantidadeIndex = headers.indexOf("Quantidade");
    const custoIndex = headers.indexOf("Custo Unitário");

    if (quantidadeIndex < 0 || custoIndex < 0) return 0;

    return rows.reduce((acc, row) => {
      const quantidade = parseQuantidade(row[quantidadeIndex]);
      const custo = parseBRLCurrency(row[custoIndex]);
      return acc + quantidade * custo;
    }, 0);
  }, [headers, rows]);

  const gerarRelatorioSaidas = useCallback(() => {
    generatePdfReport({
      reportTitle: config.reportTitle,
      fileNamePrefix: config.fileNamePrefix,
      subtitle: "Informações das Baixas de Serviço",
      headers,
      rows,
      custoColIndex: headers.indexOf("Custo Unitário"),
      enabledColumns: enabledColumnIndexes,
      footerLines: [
        `Total de Itens Baixados: ${totalProdutos}`,
        `Valor Total das Baixas: R$ ${valorTotal.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      ],
    });
  }, [
    config.reportTitle,
    config.fileNamePrefix,
    headers,
    rows,
    enabledColumnIndexes,
    totalProdutos,
    valorTotal,
  ]);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        actionKey?: string;
        label?: string;
        codigo?: string | null;
        modulo?: {
          id?: number | string | null;
          label?: string;
          codigo?: string | null;
          slug?: string;
        };
        pathContext?: {
          pathname?: string;
          selectedAddressValue?: string;
        };
      }>;

      if (custom.detail?.actionKey !== "generate_report") return;

      const currentPath = normalizeSlug(window.location.pathname);
      const eventPath = normalizeSlug(
        custom.detail?.pathContext?.pathname || ""
      );

      const moduloLabel = normalizeSlug(custom.detail?.modulo?.label || "");
      const moduloCodigo = normalizeSlug(custom.detail?.modulo?.codigo || "");
      const moduloSlug = normalizeSlug(custom.detail?.modulo?.slug || "");

      const baseSaidas = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/saidas`
      );

      const baseServicos = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/saidas/servicos`
      );

      const isSaidasContext =
        currentPath.includes(baseSaidas) ||
        currentPath.includes(baseServicos) ||
        eventPath.includes(baseSaidas) ||
        eventPath.includes(baseServicos) ||
        currentPath.includes("saidas") ||
        eventPath.includes("saidas") ||
        moduloLabel.includes("saidas") ||
        moduloLabel.includes("servicos") ||
        moduloLabel.includes("serviços") ||
        moduloCodigo.includes("saidas") ||
        moduloCodigo.includes("servicos") ||
        moduloSlug.includes("saidas") ||
        moduloSlug.includes("servicos");

      if (!isSaidasContext) return;

      gerarRelatorioSaidas();
    };

    window.addEventListener("logvi:menu-action", handler);

    return () => {
      window.removeEventListener("logvi:menu-action", handler);
    };
  }, [config.areaSlug, config.estoqueSlug, gerarRelatorioSaidas]);

  const openModalPorOS = (os: string) => {
    if (!config.enableRetornoModal) return;

    const itensDaOS: ItemOSModal[] = rows
      .map((row, index) => {
        const meta = rowMeta[index];

        if (!meta) return null;
        if ((meta.osNumero || row[0] || "") !== os) return null;

        return {
          originalIndex: index,
          row,
          meta,
        };
      })
      .filter(Boolean) as ItemOSModal[];

    const initialSelecao: SelecoesState = {};

    for (const item of itensDaOS) {
      const permitido = canRequestStatus(item.meta.retornoStatus);

      initialSelecao[item.originalIndex] = {
        marcado: permitido,
        quantidade: String(item.meta.quantidade ?? ""),
      };
    }

    setModal({
      open: true,
      os,
      itens: itensDaOS,
    });

    setSelecao(initialSelecao);
    setMotivo("");
  };

  const closeModal = () => {
    if (submitting || cancelandoIds.length > 0) return;

    setModal({
      open: false,
      os: "",
      itens: [],
    });

    setSelecao({});
    setMotivo("");
  };

  const itensSolicitaveis = useMemo(() => {
    return modal.itens.filter((item) =>
      canRequestStatus(item.meta.retornoStatus)
    );
  }, [modal.itens]);

  const itensSelecionados = useMemo(() => {
    return itensSolicitaveis.filter(
      (item) => selecao[item.originalIndex]?.marcado
    );
  }, [itensSolicitaveis, selecao]);

  const itensComSolicitacaoPendente = useMemo(() => {
    return modal.itens.filter((item) =>
      canCancelStatus(item.meta.retornoStatus)
    );
  }, [modal.itens]);

  const marcarTodos = () => {
    setSelecao((prev) => {
      const next = { ...prev };

      for (const item of itensSolicitaveis) {
        next[item.originalIndex] = {
          marcado: true,
          quantidade:
            prev[item.originalIndex]?.quantidade ||
            String(item.meta.quantidade ?? ""),
        };
      }

      return next;
    });
  };

  const desmarcarTodos = () => {
    setSelecao((prev) => {
      const next = { ...prev };

      for (const item of itensSolicitaveis) {
        next[item.originalIndex] = {
          marcado: false,
          quantidade:
            prev[item.originalIndex]?.quantidade ||
            String(item.meta.quantidade ?? ""),
        };
      }

      return next;
    });
  };

  const toggleItem = (originalIndex: number, checked: boolean) => {
    setSelecao((prev) => ({
      ...prev,
      [originalIndex]: {
        ...prev[originalIndex],
        marcado: checked,
      },
    }));
  };

  const setQuantidadeItem = (originalIndex: number, value: string) => {
    setSelecao((prev) => ({
      ...prev,
      [originalIndex]: {
        ...prev[originalIndex],
        quantidade: value,
      },
    }));
  };

  async function solicitarRetornoOS() {
    if (!itensSelecionados.length) {
      alert("Selecione ao menos um item para solicitar retorno.");
      return;
    }

    for (const item of itensSelecionados) {
      const quantidade = Number(selecao[item.originalIndex]?.quantidade);

      if (!Number.isFinite(quantidade) || quantidade <= 0) {
        alert(
          `Informe uma quantidade válida para o item ${item.meta.nomeProduto}.`
        );
        return;
      }

      if (quantidade > item.meta.quantidade) {
        alert(
          `A quantidade do item ${item.meta.nomeProduto} não pode ser maior que ${item.meta.quantidade}.`
        );
        return;
      }
    }

    setSubmitting(true);

    try {
      for (const item of itensSelecionados) {
        const quantidade = Number(selecao[item.originalIndex]?.quantidade);

        const res = await fetch(getSolicitarRetornoUrl(config), {
          method: "POST",
          headers: buildAuthHeaders(true),
          body: JSON.stringify({
            saida_movimentacao_id: item.meta.movimentacaoId,
            produto_id: item.meta.produtoId,
            quantidade,
            solicitante_responsavel_id: 1,
            solicitante_nome: "Usuário Logado",
            motivo: motivo.trim() || null,
            estoque_slug: config.estoqueSlug,
            estoqueSlug: config.estoqueSlug,
          }),
        });

        const json = await res.json().catch(() => null);

        if (!res.ok || !json?.ok) {
          throw new Error(
            json?.erro ||
              json?.erros?.join("\n") ||
              `Falha ao solicitar retorno do item ${item.meta.nomeProduto}`
          );
        }
      }

      await refresh();
      closeModal();
    } catch (e: any) {
      alert(e?.message || "Falha ao solicitar retorno.");
    } finally {
      setSubmitting(false);
    }
  }

  async function cancelarSolicitacao(item: ItemOSModal) {
    const solicitacaoId = item.meta.retornoSolicitacaoId;

    if (!solicitacaoId) {
      alert("Solicitação não encontrada para este item.");
      return;
    }

    const confirmar = window.confirm(
      `Cancelar a solicitação de retorno do item "${item.meta.nomeProduto}"?`
    );

    if (!confirmar) return;

    setCancelandoIds((prev) => [...prev, item.originalIndex]);

    try {
      const res = await fetch(getCancelarRetornoUrl(config), {
        method: "POST",
        headers: buildAuthHeaders(true),
        body: JSON.stringify({
          solicitacao_id: solicitacaoId,
          cancelado_por_id: 1,
          cancelado_por_nome: "Usuário Logado",
          estoque_slug: config.estoqueSlug,
          estoqueSlug: config.estoqueSlug,
        }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.ok) {
        throw new Error(
          json?.erro ||
            json?.erros?.join("\n") ||
            `Falha ao cancelar solicitação do item ${item.meta.nomeProduto}`
        );
      }

      await refresh();

      setModal((prev) => ({
        ...prev,
        itens: prev.itens.map((x) =>
          x.originalIndex === item.originalIndex
            ? {
                ...x,
                meta: {
                  ...x.meta,
                  retornoSolicitacaoId: null,
                  retornoStatus: "none",
                },
              }
            : x
        ),
      }));
    } catch (e: any) {
      alert(e?.message || "Falha ao cancelar solicitação.");
    } finally {
      setCancelandoIds((prev) =>
        prev.filter((id) => id !== item.originalIndex)
      );
    }
  }

  const renderModal = () => {
    if (!modal.open) return null;

    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <div style={modalHeaderStyle}>
            <h2 style={modalTitleStyle}>Solicitar retorno da OS {modal.os}</h2>

            <button
              type="button"
              onClick={closeModal}
              disabled={submitting || cancelandoIds.length > 0}
              style={buttonSecondaryStyle}
            >
              Fechar
            </button>
          </div>

          <div style={modalBodyStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
                gap: 8,
              }}
            >
              <div style={{ fontWeight: 700, color: "#0c2b53" }}>
                Itens da OS
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={marcarTodos}
                  style={buttonMutedStyle}
                >
                  Marcar todos
                </button>
                <button
                  type="button"
                  onClick={desmarcarTodos}
                  style={buttonMutedStyle}
                >
                  Desmarcar todos
                </button>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Selecionar
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Código
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Produto
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Qtd. Original
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Qtd. Retorno
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Status
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Ações
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {modal.itens.map((item) => {
                    const permitido = canRequestStatus(item.meta.retornoStatus);
                    const podeCancelar = canCancelStatus(
                      item.meta.retornoStatus
                    );
                    const cancelando = cancelandoIds.includes(
                      item.originalIndex
                    );

                    return (
                      <tr
                        key={`${item.meta.movimentacaoId}-${item.meta.produtoId}`}
                      >
                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          <input
                            type="checkbox"
                            checked={
                              selecao[item.originalIndex]?.marcado === true
                            }
                            disabled={!permitido}
                            onChange={(e) =>
                              toggleItem(item.originalIndex, e.target.checked)
                            }
                          />
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          {item.meta.codigo}
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          {item.meta.nomeProduto}
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          {item.meta.quantidade}
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          <input
                            type="number"
                            min={1}
                            max={item.meta.quantidade}
                            disabled={!permitido}
                            value={
                              selecao[item.originalIndex]?.quantidade ?? ""
                            }
                            onChange={(e) =>
                              setQuantidadeItem(
                                item.originalIndex,
                                e.target.value
                              )
                            }
                            style={{
                              ...inputStyle,
                              height: 34,
                              padding: "6px 8px",
                            }}
                          />
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          <span
                            style={getStatusBadgeStyle(item.meta.retornoStatus)}
                          >
                            {getStatusLabel(item.meta.retornoStatus)}
                          </span>
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          {podeCancelar ? (
                            <button
                              type="button"
                              onClick={() => cancelarSolicitacao(item)}
                              disabled={cancelando}
                              style={buttonDangerStyle}
                            >
                              {cancelando ? "Cancelando..." : "Cancelar"}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 16 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0c2b53",
                  marginBottom: 6,
                }}
              >
                Motivo do retorno
              </label>

              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Informe o motivo da solicitação de retorno."
                style={textareaStyle}
              />
            </div>
          </div>

          <div style={modalFooterStyle}>
            <div style={{ fontSize: 13, color: "#475569" }}>
              {itensSelecionados.length} item(ns) selecionado(s).{" "}
              {itensComSolicitacaoPendente.length > 0
                ? `${itensComSolicitacaoPendente.length} item(ns) com solicitação pendente.`
                : ""}
            </div>

            <button
              type="button"
              onClick={solicitarRetornoOS}
              disabled={submitting || itensSelecionados.length === 0}
              style={buttonPrimaryStyle}
            >
              {submitting ? "Solicitando..." : "Solicitar Retorno"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <TitleButtons fullBleedLine title={config.title} />

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {erro}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 16 }}>Carregando saídas…</div>
      ) : (
        <TableV2
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          isTableStandby
          onDoubleClick={(_viewIndex, rowData) => {
            if (!config.enableRetornoModal) return;

            const os = String(rowData[0] ?? "").trim();

            if (!os || os === "-") return;

            openModalPorOS(os);
          }}
        />
      )}

      {renderModal()}
    </div>
  );
};

export default SaidasServicoGenerico;
